import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { ExperienceButton } from "./CTA";
import { HERO_IMAGE } from "@/lib/constants";

const lines = ["YOUR FIRST RECOVERY", "SESSION IS ON US."];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }
  })
};

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 160]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.2]);

  return (
    <section id="top" className="relative flex min-h-screen flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY, backgroundColor: "#adc0d4" }} className="absolute inset-0">
        <motion.img
          src={HERO_IMAGE}
          alt="Guest welcomed with a handshake at the Rest Recovery Wellness front desk in North Phoenix"
          className="h-full w-full object-cover object-[48%_50%] md:object-contain md:object-right"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, delay: 1.1, ease: "easeOut" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(100deg, rgba(5,5,5,0.78) 0%, rgba(5,5,5,0.5) 24%, rgba(5,5,5,0.15) 42%, transparent 55%), linear-gradient(to top, rgba(5,5,5,0.45) 0%, transparent 35%)"
          }}
        />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-7xl px-5 pb-20 pt-36 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-zinc-300"
          data-testid="hero-brand-kicker"
        >
          Rest Recovery Wellness — North Phoenix
        </motion.p>

        <h1 className="font-display text-[11vw] font-black uppercase leading-[0.92] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-6 flex items-baseline gap-4"
          data-testid="hero-price-block"
        >
          <span className="font-display text-2xl font-black tracking-tighter text-zinc-500 line-through md:text-3xl">$50</span>
          <span className="font-display text-4xl font-black uppercase tracking-tighter text-rest-red md:text-6xl">Complimentary</span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg"
          data-testid="hero-subcopy"
        >
          Experience Rest Recovery Wellness with a personalized, staff-guided recovery session featuring 2–3 of our
          premium recovery services. A $50 value. Your first visit is on us.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-8"
        >
          <ExperienceButton id="hero-cta-claim" prominent />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400" data-testid="hero-microcopy">
            First-time guests only. No membership required.
          </p>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 right-6 hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 md:flex"
      >
        Scroll <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
      </motion.div>
    </section>
  );
};
